export type DemoSlotPeriod = "morning" | "afternoon" | "evening";

export type DemoSlotItem = {
  slotDate?: string;
  slotDateStr?: string;
  slotStartTime?: string;
  slotEndTime?: string;
  slotStartTimeStrInGivenTimeZone?: string;
  slotEndTimeStrInGivenTimeZone?: string;
  teacherId?: string | number | null;
  teacherSlotInfoId?: string | number | null;
  uniqueId: string;
  [key: string]: unknown;
};

export type DemoSlotDay = {
  slotDate: string;
  slotDateStr: string;
  morningSlots: DemoSlotItem[];
  afternoonSlots: DemoSlotItem[];
  eveningSlots: DemoSlotItem[];
};

const AFTERNOON_HOURS = [12, 1, 2, 3, 4, 5, 6];
const EVENING_HOURS = [7, 8, 9, 10, 11];

function slotUniqueId(slot: any, group?: any): string {
  return `${slot.teacherSlotInfoId || ""}-${slot.slotDate || group?.slotDate || ""}-${slot.slotStartTime || ""}`;
}

function enrichSlot(slot: any, group?: any): DemoSlotItem {
  return {
    ...slot,
    slotDate: slot.slotDate || group?.slotDate,
    slotDateStr: slot.slotDateStr || group?.slotDateStr || slot.slotDate || group?.slotDate,
    uniqueId: slotUniqueId(slot, group),
  };
}

export function categorizeSlots(slots: DemoSlotItem[]): Pick<DemoSlotDay, "morningSlots" | "afternoonSlots" | "eveningSlots"> {
  const morningSlots: DemoSlotItem[] = [];
  const afternoonSlots: DemoSlotItem[] = [];
  const eveningSlots: DemoSlotItem[] = [];

  const sorted = [...slots].sort((a, b) => {
    const ah = parseInt(String(a.slotStartTime || "0").split(":")[0], 10);
    const bh = parseInt(String(b.slotStartTime || "0").split(":")[0], 10);
    return ah - bh;
  });

  sorted.forEach((slot) => {
    const label = String(slot.slotStartTimeStrInGivenTimeZone || "");
    const parts = label.split(" ");
    const hourToken = parts[0] || "";
    const meridiem = (parts[1] || "").toLowerCase();
    const hour = parseInt(hourToken, 10);

    if (meridiem.includes("pm") && AFTERNOON_HOURS.includes(hour)) {
      afternoonSlots.push(slot);
    } else if (meridiem.includes("pm") && EVENING_HOURS.includes(hour)) {
      eveningSlots.push(slot);
    } else {
      morningSlots.push(slot);
    }
  });

  return { morningSlots, afternoonSlots, eveningSlots };
}

export function normalizeDemoSlotDays(data: unknown): DemoSlotDay[] {
  const groups = Array.isArray(data) ? data : Object.values((data as Record<string, unknown>) || {});

  const toSortableDateKey = (day: DemoSlotDay): string => {
    const candidates = [day.slotDate, day.slotDateStr].filter(Boolean).map(String);

    for (const raw of candidates) {
      // YYYY-MM-DD
      if (/^\d{4}-\d{2}-\d{2}/.test(raw)) {
        return raw.slice(0, 10);
      }
      // DD/MM/YYYY
      if (raw.includes("/")) {
        const [d, m, y] = raw.split("/");
        if (d && m && y) {
          return `${y.padStart(4, "0")}-${m.padStart(2, "0")}-${d.padStart(2, "0")}`;
        }
      }
      // "Mon Aug 03" / "Aug 03 2026" etc.
      const withYear = /\d{4}/.test(raw) ? raw : `${raw} ${new Date().getFullYear()}`;
      const parsed = Date.parse(withYear);
      if (!Number.isNaN(parsed)) {
        const dt = new Date(parsed);
        const yyyy = dt.getFullYear();
        const mm = String(dt.getMonth() + 1).padStart(2, "0");
        const dd = String(dt.getDate()).padStart(2, "0");
        return `${yyyy}-${mm}-${dd}`;
      }
    }

    return candidates[0] || "";
  };

  const days = groups
    .map((group: any) => {
      const merged = [
        ...(group?.morningSlots || []),
        ...(group?.afternoonSlots || []),
        ...(group?.eveningSlots || []),
        ...(group?.slots || []),
      ].map((slot: any) => enrichSlot(slot, group));

      const slotDateStr = String(group?.slotDateStr || group?.slotDate || merged[0]?.slotDateStr || "");
      if (!slotDateStr) return null;

      const categorized = categorizeSlots(merged);
      return {
        slotDate: String(group?.slotDate || slotDateStr),
        slotDateStr,
        ...categorized,
      };
    })
    .filter(Boolean) as DemoSlotDay[];

  return days.sort((a, b) => toSortableDateKey(a).localeCompare(toSortableDateKey(b)));
}

export function toDdMmYyyy(input?: string): string {
  if (!input) return "";
  if (input.includes("/")) return input;
  const [y, m, d] = input.split("-");
  return y && m && d ? `${d}/${m}/${y}` : input;
}

export type DemoCourseOption = {
  key: string;
  label: string;
  name: string;
  parentCourse: any;
  tag?: any;
  v2Id?: string | number;
  id?: string | number;
  demoCourseId?: string | number;
};

const isKaraokeCourse = (course: any): boolean => {
  const name = String(course?.name ?? course?.localizedName ?? "").toLowerCase();
  const batName = String(course?.batName ?? "").toLowerCase();
  return (
    name.includes("karaoke") ||
    batName === "karaoke" ||
    course?.subCategory === "karaokeSkill" ||
    course?.type === "Karaoke"
  );
};

/** Flatten config courses; Karaoke expands to one option per language tag. */
export function expandDemoCourseOptions(courses: any[]): DemoCourseOption[] {
  const options: DemoCourseOption[] = [];

  for (const course of courses || []) {
    if (isKaraokeCourse(course) && Array.isArray(course.tags) && course.tags.length > 0) {
      course.tags.forEach((tag: any) => {
        const language = String(tag?.localizedName || tag?.name || "").trim();
        if (!language) return;
        options.push({
          key: `karaoke-${tag.v2Id ?? tag.id ?? language}`,
          label: `Karaoke - ${language}`,
          name: course.name,
          parentCourse: course,
          tag,
          v2Id: tag.v2Id,
          id: tag.id,
          demoCourseId: tag.demoCourseId,
        });
      });
      continue;
    }

    options.push({
      key: String(course.v2Id ?? course.id ?? course.name),
      label: formatCourseLabel(course.name) || String(course.name || ""),
      name: course.name,
      parentCourse: course,
      v2Id: course.v2Id,
      id: course.id,
      demoCourseId: course.demoCourseId,
    });
  }

  return options;
}

export function formatCourseLabel(name?: string): string {
  return name ? String(name) : "";
}

/**
 * Student courses API `parentTagId` matches config course option `id`.
 * Disable when that demo course is already completed so it can't be rebooked.
 */
export function isDemoCourseAlreadyCompleted(
  option: DemoCourseOption,
  studentCourses: any[] | null | undefined
): boolean {
  if (!Array.isArray(studentCourses) || studentCourses.length === 0) return false;

  const optionId = option.id != null && String(option.id).trim() !== "" ? String(option.id) : "";
  if (!optionId) return false;

  return studentCourses.some((course) => {
    const isDemo =
      course?.isDemoCourse === true ||
      course?.isDemoCourse === "true" ||
      course?.isDemocourseId === true ||
      course?.isDemoCourseId === true;
    if (!isDemo) return false;

    const completed = Number(course?.completedClasses ?? 0);
    if (!Number.isFinite(completed) || completed <= 0) return false;

    const parentTagId =
      course?.parentTagId ??
      course?.tagsMap?.parentTagId ??
      course?.tagId ??
      course?.parentTag?.id;

    return parentTagId != null && String(parentTagId) === optionId;
  });
}

/** tagId for allocateTeacherForBooking must be course/tag `id` (legacy admin), not demoCourseId. */
export function resolveCourseTagId(course: any, fallback?: string | number): string {
  const tag =
    course?._demoTag ??
    course?.tag ??
    course?.tags?.[0];

  const tagId =
    tag?.id ??
    course?.id ??
    tag?.v2Id ??
    course?.v2Id ??
    fallback;

  return tagId != null && String(tagId).trim() !== "" ? String(tagId) : "1";
}

export function toSelectedDemoCourse(option: DemoCourseOption): any {
  return {
    ...option.parentCourse,
    name: option.name,
    v2Id: option.v2Id,
    id: option.id,
    demoCourseId: option.demoCourseId,
    _demoTag: option.tag,
    _demoLabel: option.label,
  };
}

export function isSameDemoCourseSelection(selected: any, option: DemoCourseOption): boolean {
  if (!selected) return false;
  if (option.tag) {
    return String(selected?._demoTag?.v2Id ?? selected?._demoTag?.id) === String(option.tag.v2Id ?? option.tag.id);
  }
  return String(selected?.v2Id ?? selected?.id) === String(option.v2Id ?? option.id);
}

export function buildAllocateTeacherPayload(options: {
  mode: "book" | "reschedule";
  slot: DemoSlotItem;
  tagId: string;
  userId: string | number;
  classScheduleId?: string | number | null;
  rescheduleReason?: string;
}) {
  const { mode, slot, tagId, userId, classScheduleId, rescheduleReason } = options;
  const endTime = slot.slotEndTime === "00:00:00" ? "24:00:00" : slot.slotEndTime;
  const bookingDate = toDdMmYyyy(slot.slotDate || slot.slotDateStr);
  const isPaidTeacherSlot = Boolean(slot.teacherId && String(slot.teacherId) !== "0");

  if (mode === "book") {
    return {
      bookingClassEndTime: endTime,
      bookingClassStartTime: slot.slotStartTime,
      bookingDate,
      bookingType: "",
      isDemoStudent: true,
      isFreeStudent: false,
      isPaidStudent: false,
      studentId: null,
      isPaidTeacherSlot,
      userId: String(userId),
      voucherCode: "",
      languageId: "1",
      tagId,
      classToCancelId: "",
      // Mixpanel / attribution for bookings created from admin CMS
      utmSource: "admintool",
    };
  }

  const scheduleId = classScheduleId != null ? String(classScheduleId) : "";

  return {
    bookingClassEndTime: endTime,
    bookingClassStartTime: slot.slotStartTime,
    bookingDate,
    bookingType: "reschedule",
    isDemoStudent: true,
    isFreeStudent: false,
    isPaidStudent: false,
    studentId: null,
    teacherId: slot.teacherId || null,
    isPaidTeacherSlot,
    userId: String(userId),
    voucherCode: "",
    languageId: "1",
    tagId,
    classScheduleId: scheduleId,
    classToCancelId: scheduleId,
    ...(rescheduleReason ? { rescheduleReason } : {}),
  };
}
